import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Plus, Trash2, Edit2, Loader2, Calendar, ClipboardList, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import Layout from "../components/Layout";
import { fmtDateMed, fmtRange } from "../lib/dates";

const COLORS = ["#F59E0B", "#EF4444", "#10B981", "#3B82F6", "#A78BFA", "#EC4899", "#06B6D4", "#84CC16"];

const Operations = () => {
  const [ops, setOps] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    shift_id: "",
    staff_required: 1,
    is_hard: true,
    color: COLORS[0],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Format a Date as YYYY-MM-DD using LOCAL time (toISOString shifts dates in non-UTC timezones)
  const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Build [start..end] inclusive list of YYYY-MM-DD strings
  const expandRange = (start, end) => {
    if (!start) return [];
    const out = [];
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${(end || start)}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return [];
    if (e < s) return [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      out.push(fmtLocal(d));
    }
    return out;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [opsRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/operations`),
        axios.get(`${API}/shifts`),
      ]);
      setOps(opsRes.data || []);
      setShifts(shiftsRes.data || []);
    } catch {
      toast.error("Failed to load operations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    const today = fmtLocal(new Date());
    setForm({
      name: "",
      start_date: today,
      end_date: today,
      shift_id: shifts[0]?.shift_id || "",
      staff_required: 1,
      is_hard: true,
      color: COLORS[0],
      notes: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (op) => {
    setEditing(op);
    const sorted = [...(op.dates || [])].sort();
    setForm({
      name: op.name,
      start_date: sorted[0] || "",
      end_date: sorted[sorted.length - 1] || sorted[0] || "",
      shift_id: op.shift_id,
      staff_required: op.staff_required,
      is_hard: op.is_hard,
      color: op.color || COLORS[0],
      notes: op.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.shift_id) { toast.error("Pick a shift"); return; }
    if (!form.start_date) { toast.error("Pick a start date"); return; }
    const endDate = form.end_date || form.start_date;
    if (endDate < form.start_date) { toast.error("End date must be on or after the start date"); return; }
    const expanded = expandRange(form.start_date, endDate);
    if (expanded.length === 0) { toast.error("Invalid date range"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        dates: expanded,
        shift_id: form.shift_id,
        staff_required: Math.max(1, parseInt(form.staff_required, 10) || 1),
        is_hard: !!form.is_hard,
        color: form.color,
        notes: form.notes.trim(),
      };
      if (editing) {
        await axios.put(`${API}/operations/${editing.operation_id}`, payload);
        toast.success("Operation updated");
      } else {
        await axios.post(`${API}/operations`, payload);
        toast.success(`Operation created — applied to ${expanded.length} day${expanded.length === 1 ? "" : "s"}`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (op) => {
    if (!window.confirm(`Delete operation "${op.name}"?`)) return;
    try {
      await axios.delete(`${API}/operations/${op.operation_id}`);
      toast.success("Operation deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const shiftName = (id) => shifts.find((s) => s.shift_id === id)?.name || "—";

  return (
    <Layout>
      <div className="space-y-6" data-testid="operations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#002FA7] dark:text-blue-400" />
            Operations
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Date-bound staffing requirements (court attendance, training days, events). The engine treats <strong>hard</strong> operations as a per-date floor on min_staff for the chosen shift.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded inline-flex items-center gap-2"
          data-testid="add-operation-btn"
        >
          <Plus className="w-4 h-4" /> Add operation
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
      ) : ops.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-dashed border-zinc-300 dark:border-zinc-700 p-12 text-center" data-testid="operations-empty">
          <ClipboardList className="w-12 h-12 mx-auto text-zinc-300 dark:text-zinc-700 mb-3" />
          <p className="text-sm text-zinc-600 dark:text-zinc-400">No operations yet. Add one to bump staffing for specific dates.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-left py-3 px-4">Operation</th>
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-left py-3 px-4">Dates</th>
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-left py-3 px-4">Shift</th>
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-left py-3 px-4">Staff</th>
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-left py-3 px-4">Rule</th>
                <th className="text-xs uppercase tracking-[0.18em] font-medium text-zinc-500 text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {ops.map((op) => (
                <tr key={op.operation_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50" data-testid={`operation-row-${op.operation_id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: op.color }} title={op.color} />
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{op.name}</span>
                    </div>
                    {op.notes && <p className="text-xs text-zinc-500 mt-0.5 ml-5">{op.notes}</p>}
                  </td>
                  <td className="py-3 px-4 text-sm text-zinc-700 dark:text-zinc-300 tabular-nums">
                    {(() => {
                      const sorted = [...(op.dates || [])].sort();
                      if (sorted.length === 0) return "—";
                      if (sorted.length === 1) return fmtDateMed(sorted[0]);
                      return `${fmtRange(sorted[0], sorted[sorted.length - 1])} (${sorted.length} days)`;
                    })()}
                  </td>
                  <td className="py-3 px-4 text-sm text-zinc-700 dark:text-zinc-300">{shiftName(op.shift_id)}</td>
                  <td className="py-3 px-4 text-sm tabular-nums text-zinc-700 dark:text-zinc-300">{op.staff_required}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${op.is_hard ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"}`}>
                      {op.is_hard ? "Hard" : "Soft"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(op)} className="p-1.5 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100" data-testid={`edit-operation-${op.operation_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(op)} className="p-1.5 text-zinc-500 hover:text-red-600" data-testid={`delete-operation-${op.operation_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="operation-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit operation" : "New operation"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Court attendance"
                className="w-full mt-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100"
                data-testid="op-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                <Calendar className="w-3 h-3" /> Date range *
              </label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <div>
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400">From</span>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => {
                      const next = { ...form, start_date: e.target.value };
                      // If end is empty or before new start, snap end = start
                      if (!form.end_date || form.end_date < e.target.value) {
                        next.end_date = e.target.value;
                      }
                      setForm(next);
                    }}
                    className="w-full mt-0.5 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100"
                    data-testid="op-date-from-input"
                  />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400">To</span>
                  <input
                    type="date"
                    value={form.end_date}
                    min={form.start_date || undefined}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="w-full mt-0.5 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100"
                    data-testid="op-date-to-input"
                  />
                </div>
              </div>
              {form.start_date && (() => {
                const count = expandRange(form.start_date, form.end_date || form.start_date).length;
                return (
                  <p className="text-[11px] text-zinc-400 mt-1.5" data-testid="op-date-range-summary">
                    {count > 0 ? `Applies to ${count} day${count === 1 ? "" : "s"}.` : "Invalid date range — end must be on or after start."}
                  </p>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-500">Shift *</label>
                <select
                  value={form.shift_id}
                  onChange={(e) => setForm({ ...form, shift_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100"
                  data-testid="op-shift-select"
                >
                  <option value="">Select shift…</option>
                  {shifts.map((s) => <option key={s.shift_id} value={s.shift_id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-500">Staff required *</label>
                <input
                  type="number"
                  min="1"
                  value={form.staff_required}
                  onChange={(e) => setForm({ ...form, staff_required: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100"
                  data-testid="op-staff-required"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500">Colour</label>
              <div className="flex gap-2 mt-1.5">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded ${form.color === c ? "ring-2 ring-offset-2 ring-zinc-900 dark:ring-zinc-100" : ""}`}
                    style={{ backgroundColor: c }}
                    data-testid={`op-color-${c}`}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                <ShieldAlert className="w-3 h-3" /> Rule type
              </label>
              <div className="flex gap-2 mt-1.5">
                <button
                  onClick={() => setForm({ ...form, is_hard: true })}
                  className={`flex-1 px-3 py-2 text-sm rounded border ${form.is_hard ? "bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300" : "border-zinc-200 dark:border-zinc-700 text-zinc-500"}`}
                  data-testid="op-rule-hard"
                >
                  <strong>Hard</strong> — enforce
                </button>
                <button
                  onClick={() => setForm({ ...form, is_hard: false })}
                  className={`flex-1 px-3 py-2 text-sm rounded border ${!form.is_hard ? "bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300" : "border-zinc-200 dark:border-zinc-700 text-zinc-500"}`}
                  data-testid="op-rule-soft"
                >
                  <strong>Soft</strong> — preferred
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Optional context for this operation…"
                className="w-full mt-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 dark:text-zinc-100 resize-none"
                data-testid="op-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 sm:col-span-2">
              <button onClick={() => setDialogOpen(false)} className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded text-zinc-700 dark:text-zinc-300" data-testid="op-cancel-btn">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="op-save-btn"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default Operations;
